<script setup lang="ts">
import { setSEO } from '~/shared/utils/setSEO'
import type { PageMatter } from '~/shared/utils/defaultDataSchema'
import type { Ref } from 'vue'
import { useRoute } from 'vue-router'

const activeId: Ref<string | undefined> = ref(undefined)
const route = useRoute()

const { data: page } = await useAsyncData(route.path, () => {
  return queryCollection('content').path(route.path).first()
});

const links = page.value && page.value.body && page.value.body.toc ? page.value.body.toc.links : [];

const seoSettings = setSEO(page?.value || {}, route.path)
const pageData: PageMatter  = seoSettings.pageData;

logger.debug("Page data guide:", pageData.guide, pageData.peer_order);

useHead(seoSettings.headData)
useSeoMeta(seoSettings.seoMetaData)

onMounted(() => {

  const callback = (entries: IntersectionObserverEntry[]) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        activeId.value = entry.target.id
        break;
      }
    }
  }
  const observer = new IntersectionObserver(callback, {
    root: null,
    threshold: 0.5
  })
  const elements = document.querySelectorAll('h2, h3')

  for (const element of elements) {
    observer.observe(element)
  }

  onBeforeUnmount(() => {
    for (const element of elements) {
      observer.unobserve(element)
    }
  })
})

</script>

<template>
<main>
  <article v-if="page" class="mx-auto">
    <div class="grid grid-cols-10 gap-4">
      <div class="col-span-10 prose dark:prose-invert lg:prose-xl mx-auto">
        <span v-if="!pageData.is_manual_title">
          <h1 class="article-header text-center" >{{ pageData.title }}</h1>
          <div v-if="pageData.author || pageData.date_created" class='text-sm text-center mb-1'>
            <span v-if="pageData.author">by {{ pageData.author }}</span>
            <span v-if="pageData.date_created">&nbsp;{{  new Date(pageData.date_created).toLocaleDateString() }}</span>
          </div>
        </span>
        <div v-if="pageData.image && !pageData.is_manual_image" class="not-prose">
          <img :src="pageData.image" :alt="pageData.image_alt" class="mx-auto max-h-52">
        </div>
      </div>
      <div 
        class="prose dark:prose-invert lg:prose-xl prose-code:bg-gray-100 dark:prose-code:bg-black prose-pre:bg-gray-100 dark:prose-pre:bg-black mr-8 md:mr-4"
        :class="{'col-span-10 md:col-span-7' : pageData.is_toc, 'col-span-10' : !pageData.is_toc}"
      >
        <ContentRenderer :value="page" />
      </div>
      <div v-if="pageData.is_toc" class="hidden md:col-span-3 md:block">
        <aside class="sticky top-8">
            <ChapterNav 
              v-if="pageData.guide"
              search-path="/projects/jamstart"
              :search-guide="pageData.guide"
              :current-order="pageData.peer_order.toString()"
            />
          <div class="font-semibold mb-2">
            <NuxtLink 
              :to="{path: route.path, hash: ''}"
            >
              Table of Contents
            </NuxtLink>
          </div>
          <nav>
            <TocLinks :links="links" :active-id="activeId" />
          </nav>
        </aside>
      </div>
      <div class="col-span-10">
        <div class="mt-2 text-center">
          <span class="inline-flex socialsharebank">
            <SocialShare
              v-for="network in ['facebook', 'x', 'linkedin', 'email']"
              :key="network"
              :label="false"
              :network="network"
            />
          </span>
        </div>
      </div>
    </div>
  </article>
  <div v-else class="mx-auto text-center">
    <h1>Document not found (404)</h1>
    <p>This document {{ route.path }} could not be found</p>
    <NuxtLink to="/" class="text-blue-500 hover:underline">Go back home</NuxtLink>
  </div>
</main>
</template>

<style scoped>
h1.article-header {
  margin-bottom: 2vh;
}
.socialsharebank > a {
  @apply mr-3
}
</style>